/**
 * ArtefactModal Component
 *
 * Full-screen modal for viewing artefacts on mobile devices.
 * Provides a touch-friendly interface with swipe gestures.
 */

import React, { useState, useRef, useEffect } from "react";
import { Modal, ModalContent, ModalHeader, ModalBody, Button } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";

import { ArtefactsPanel } from "./ArtefactsPanel";

interface ArtefactModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ArtefactModal: React.FC<ArtefactModalProps> = ({ isOpen, onOpenChange }) => {
  const { t } = useTranslation();
  const [swipeY, setSwipeY] = useState(0);
  const [isSwipeable, setIsSwipeable] = useState(true);
  const startYRef = useRef(0);
  const modalContentRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Handle touch events for swipe-to-close
  const handleTouchStart = (e: React.TouchEvent) => {
    // Only allow swipe from the header area
    const target = e.target as HTMLElement;
    const header = target.closest(".modal-header");

    if (header) {
      startYRef.current = e.touches[0].clientY;
      setIsSwipeable(true);
    } else {
      setIsSwipeable(false);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isSwipeable) return;

    const currentY = e.touches[0].clientY;
    const diff = currentY - startYRef.current;

    // Only track downward swipes
    if (diff > 0) {
      setSwipeY(Math.min(diff, 200)); // Cap at 200px

      // Apply visual feedback
      if (modalContentRef.current) {
        modalContentRef.current.style.transform = `translateY(${diff * 0.3}px)`;
        modalContentRef.current.style.opacity = `${1 - diff / 500}`;
      }
    }
  };

  const handleTouchEnd = () => {
    if (!isSwipeable) return;

    // Close if swiped more than 100px
    if (swipeY > 100) {
      onOpenChange(false);
    }

    // Reset visual state
    if (modalContentRef.current) {
      modalContentRef.current.style.transform = "";
      modalContentRef.current.style.opacity = "";
    }

    setSwipeY(0);
    setIsSwipeable(true);
  };

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setSwipeY(0);
      if (modalContentRef.current) {
        modalContentRef.current.style.transform = "";
        modalContentRef.current.style.opacity = "";
      }
    }
  }, [isOpen]);

  // Focus management
  useEffect(() => {
    if (isOpen) {
      // Store current focus
      previousFocusRef.current = document.activeElement as HTMLElement;

      // Focus close button after modal animation
      setTimeout(() => {
        closeButtonRef.current?.focus();
      }, 100);
    } else {
      // Return focus to previous element
      previousFocusRef.current?.focus();
    }
  }, [isOpen]);

  return (
    <Modal
      hideCloseButton
      classNames={{
        base: "m-0 sm:m-0",
        wrapper: "p-0",
        body: "p-0",
        header: "border-b border-divider modal-header",
      }}
      isOpen={isOpen}
      scrollBehavior="inside"
      size="full"
      onOpenChange={onOpenChange}
    >
      <ModalContent>
        {(onClose) => (
          <div
            ref={modalContentRef}
            className="h-full transition-transform transition-opacity"
            style={{ transition: "transform 0.2s ease-out, opacity 0.2s ease-out" }}
            onTouchEnd={handleTouchEnd}
            onTouchMove={handleTouchMove}
            onTouchStart={handleTouchStart}
          >
            <ModalHeader className="flex items-center justify-between relative pt-4">
              {/* Swipe indicator */}
              <div
                aria-hidden="true"
                className="absolute top-2 left-1/2 transform -translate-x-1/2 w-12 h-1 bg-default-400 rounded-full"
              />
              <span className="text-lg font-semibold !text-black dark:!text-white">
                {t("Generated Content & Citations")}
              </span>
              <Button
                ref={closeButtonRef}
                isIconOnly
                aria-label={t("Close artefacts modal")}
                size="sm"
                variant="light"
                onPress={onClose}
              >
                <Icon icon="solar:close-circle-line-duotone" width={20} />
              </Button>
            </ModalHeader>
            <ModalBody className="p-0">
              <ArtefactsPanel />
            </ModalBody>
          </div>
        )}
      </ModalContent>
    </Modal>
  );
};
